import { useState, useCallback, ReactNode, RefObject, useEffect } from 'react';
import cn from 'classnames';
import throttle from 'lodash.throttle';
import { useEvent } from 'react-use';

import { slugifyHeading } from '@/utils/blog';

type LabelProps = {
  children: ReactNode;
};

export function Label({ children }: LabelProps) {
  return (
    <h4 className="tracking-wider text-haiti uppercase text-xs font-medium pl-2 mb-4">
      {children}
    </h4>
  );
}

// Used to calculate each heading's offset from the top of the page.
// This will be compared to window.scrollY to determine which heading
// is currently active.
const accumulateOffsetTop = (
  el: HTMLHeadingElement,
  totalOffset = 0,
): number => {
  while (el) {
    totalOffset += el.offsetTop - el.scrollTop + el.clientTop;
    el = el.offsetParent as HTMLHeadingElement;
  }

  return totalOffset;
};

type TOCProps = {
  links: {
    title: string;
    depth: number;
  }[];
  contentRef: RefObject<HTMLElement>;
  className?: string;
};

export default function TOC({ links, contentRef, className }: TOCProps) {
  const [activeHeading, setActiveHeading] = useState<number | null>(null);
  const [nodes, setNodes] = useState<HTMLHeadingElement[]>([]);

  useEffect(() => {
    const allHeadings = contentRef.current?.querySelectorAll(`h2`);
    const headingNodes = allHeadings ? Array.from(allHeadings) : [];

    setNodes(headingNodes);
    setActiveHeading(null);
  }, [contentRef, links]);

  const scrollHandler = useCallback(
    throttle(() => {
      if (nodes.length > 0) {
        // Offsets need to be recomputed because lazily-loaded
        // content increases offsets as user scrolls down.
        const offsets = nodes
          .map((el) => {
            const accumulatedOffset = accumulateOffsetTop(el);

            if (accumulatedOffset > 0) return accumulatedOffset;

            return null;
          })
          .filter(Boolean);

        if (offsets.length > 0) {
          const activeIndex = offsets.findIndex((offset) => {
            if (offset) {
              const position = window.scrollY + 0.8 * window.innerHeight;
              return offset > position;
            }

            return false;
          });

          setActiveHeading(
            activeIndex === -1 ? links.length - 1 : activeIndex - 1,
          );
        }
      }
    }, 200),
    [nodes],
  );

  useEvent(`scroll`, scrollHandler, window, { capture: true });

  return (
    <nav className={cn(`relative rounded-md w-full`, className)}>
      <Label>Table of contents</Label>

      <ul className="w-full">
        {links.map(({ title }, index) => {
          const slug = slugifyHeading(title);
          const isActive = activeHeading === index;

          return (
            <li
              key={index}
              className={cn(
                `transition rounded-r`,
                isActive && `border-cornflower bg-catskill-white border-l-2`,
                !isActive && `border-pigeon-post border-l`,
              )}
            >
              <a
                className={cn(
                  `text-[13px] leading-5 transition w-full h-full block py-2 px-4`,
                  isActive && `text-zodiac font-semibold`,
                  !isActive && `text-light-haiti`,
                )}
                href={`#${slug}`}
              >
                {title}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
